import { useRef, useState } from "react";
import { useFormik } from "formik";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import {
  useCreateFinanceCategoryMutation,
  useCreateFinanceEntryMutation,
  useFetchFinanceAccountsQuery,
  useFetchFinanceCategoriesQuery,
  usePermission,
  useToast,
} from "@/hooks";
import { CreateFinanceEntryRequestDTO } from "@/types";
import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
  DatePicker,
  Input,
  Label,
  DrawerFooter,
  DrawerClose,
  Button,
  MoneyInput,
  MultiSelect,
  Option,
} from "@/components/ui";

import { FinanceEntryValidationSchema } from "@/shared";
import { DisplayError, formatDateToBackend, isDateValid } from "@/utils";
import { defaultEntriesCategories, defaultFinancialAccounts } from "@/helpers";

import { MessageError } from "../../message-error";
import { useQuery } from "@tanstack/react-query";
import { httpRequest } from "@/lib";

type Props = {
  handleClose: () => void;
  patientId?: string;
};

export function FormCreateFinanceEntry({ handleClose, patientId }: Props) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");

  const { toast } = useToast();
  const { id: accountId } = useParams<{ id: string }>();

  const [selectedAccount, setSelectedAccount] = useState<Option | undefined>();

  const { mutateAsync, isLoading } = useCreateFinanceEntryMutation();
  const { data: dataAccounts, isLoading: isLoadingAccounts } =
    useFetchFinanceAccountsQuery({ page });
  const { data: dataCategories, isLoading: isLoadingCategories } =
    useFetchFinanceCategoriesQuery({});

  const [file, setFile] = useState<File | null>(null);
  const idempotencyKey = useRef(crypto.randomUUID());

  const { checkPermission } = usePermission();
  // O-17 (SPEC-NAV-01 §5): the category is a dependency of this entry. The
  // create endpoint (`StoreFinanceCategorieEntryRequest`) requires `name` and
  // nothing else, so the entry's `type` is not part of the payload.
  const canCreateCategory = checkPermission("category", "create categories");
  const { mutateAsync: createCategory } = useCreateFinanceCategoryMutation();
  const [createdCategories, setCreatedCategories] = useState<Option[]>([]);
  const [categoryCreateError, setCategoryCreateError] = useState<string | null>(
    null
  );

  async function handleCreateCategory(name: string): Promise<Option> {
    setCategoryCreateError(null);
    try {
      const { data } = await createCategory({ name });
      const option: Option = { value: data.id, label: data.name };
      setCreatedCategories((current) => [...current, option]);
      return option;
    } catch (error) {
      const message = DisplayError(error, t("MESSAGE.error_message"));
      setCategoryCreateError(message);
      toast({
        title: t("LABEL.error"),
        description: message,
        variant: "destructive",
      });
      throw error;
    }
  }

  const initialValues = {
    type: "",
    description: "",
    date: "",
    value: "",
    fine_for_delay: "",
    interest_rate: "",
    payment_method: "Pix",
    pay: "0",
    payday: "",
    mode: "unique",
    entry: "",
    accountId: accountId || "",
    categoryId: "",
    patientId: patientId || "",
    procedureId: "",
    employeeId: "",
    type_periodic: "",
    total_installments: "",
    attachment_path: "",
    attachment_description: "",
  };

  const { handleSubmit, getFieldProps, errors, touched, resetForm, ...formik } =
    useFormik<CreateFinanceEntryRequestDTO>({
      initialValues,
      validationSchema: FinanceEntryValidationSchema(),
      onSubmit: async (financeEntryData) => {
        await createFinanceEntry(financeEntryData);
      },
    });

  async function createFinanceEntry(entryData: CreateFinanceEntryRequestDTO) {
    try {
      if (entryData.entry) entryData.entry = String(entryData.entry);

      if (!isDateValid(entryData.payday)) entryData.payday = "";

      entryData.value = String(entryData.value);
      entryData.interest_rate = String(entryData.interest_rate);
      entryData.fine_for_delay = entryData.fine_for_delay?.replace("%", "");
      const fd = new FormData();
      fd.append("idempotency_key", idempotencyKey.current);
      fd.append("type", entryData.type);
      fd.append("description", entryData.description);
      fd.append("date", formatDateToBackend(entryData.date));
      fd.append("value", entryData.value);
      fd.append("fine_for_delay", entryData.fine_for_delay || "");
      fd.append("interest_rate", entryData.interest_rate || "");
      fd.append("payment_method", entryData.payment_method || "");
      fd.append("pay", entryData.pay || "");
      fd.append("payday", formatDateToBackend(entryData.payday || "") || "");
      fd.append("mode", entryData.mode);
      fd.append("entry", entryData.entry || "");
      fd.append("accountId", entryData.accountId);
      fd.append("categoryId", entryData.categoryId);
      fd.append("patientId", entryData.patientId || "");
      fd.append("procedureId", entryData.procedureId || "");
      fd.append("type_periodic", entryData.type_periodic || "");
      fd.append("total_installments", entryData.total_installments || "");
      fd.append(
        "attachment_description",
        entryData.attachment_description || ""
      );
      fd.append("attachment_path", file || "");
      await mutateAsync(fd, {
        onSuccess: (response) => {
          const { message } = response;
          resetForm();
          idempotencyKey.current = crypto.randomUUID();
          handleClose();
          toast({
            title: t("LABEL.success"),
            description: t(`MESSAGE.${message.toLowerCase()}`),
          });
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    } catch (error: any) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  useQuery({
    queryKey: ["finance-account", formik.values.accountId],
    queryFn: async () => {
      if (!formik.values.patientId) return null;
      const { data } = await httpRequest.get(
        `/finance-accounts/${formik.values.patientId}`
      );
      return data;
    },
    enabled: !!formik.values.accountId && !selectedAccount,
    onSuccess: (data) => {
      if (data) {
        setSelectedAccount({
          value: data.id,
          label: data.name,
        });
      }
    },
  });

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="max-2xl:h-[94vh] max-sm:h-[90vh] max-2xl:overflow-y-auto max-2xl:pb-10"
    >
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        <div className="space-x-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1">
              <Label>{t("LABEL.account")}</Label>

              <MultiSelect
                options={
                  dataAccounts?.items.map((account) => ({
                    value: account.id,
                    label: defaultFinancialAccounts.includes(account.name)
                      ? t(account.name.toLowerCase())
                      : account.name,
                  })) || []
                }
                loading={isLoadingAccounts || !dataAccounts}
                selectedOptions={selectedAccount ? [selectedAccount] : []}
                placeholder="Selecione uma conta..."
                isInvalid={touched.accountId && errors.accountId}
                isValid={touched.accountId && !errors.accountId}
                value={formik.values.accountId}
                onChange={(value) => {
                  formik.setFieldValue("accountId", value);
                  const selected = dataAccounts?.items.find(
                    (p) => p.id === value
                  );
                  if (selected) {
                    setSelectedAccount({
                      value: selected.id,
                      label: selected.name,
                    });
                  }
                }}
                onBlur={() => formik.handleBlur("accountId")}
                messageError={errors.accountId}
                disabled={!patientId}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("LABEL.financy_account_type")}</Label>

              <div className="space-y-1">
                <Select
                  {...getFieldProps("type")}
                  onValueChange={(value) => {
                    formik.setFieldValue("type", value);
                  }}
                  defaultValue={initialValues.type}
                >
                  <SelectTrigger
                    isInvalid={touched.type && errors.type}
                    isValid={touched.type && !errors.type}
                  >
                    <SelectValue placeholder="  Selecionar ..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="income">
                        {t("LABEL.income")}
                      </SelectItem>
                      <SelectItem value="expense">
                        {t("LABEL.expense")}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <MessageError
                  isInvalid={touched.type && errors.type}
                  messageError={errors.type}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label required>{t("LABEL.category")}</Label>
              <p className="text-xs text-app-fg-muted">
                Categorias organizam receitas e despesas. O tipo do lançamento define a movimentação.
              </p>

              <MultiSelect
                options={
                  dataCategories?.map((category) => ({
                    value: category.id,
                    label: defaultEntriesCategories.includes(category.name.toLowerCase())
                      ? t(`LABEL.${category.name.toLowerCase()}`)
                      : category.name,
                  })) || []
                }
                selectedOptions={createdCategories}
                loading={isLoadingCategories || !dataCategories}
                placeholder="Selecione a categoria..."
                isInvalid={
                  Boolean(categoryCreateError) ||
                  (touched.categoryId && errors.categoryId)
                }
                isValid={
                  !categoryCreateError && touched.categoryId && !errors.categoryId
                }
                value={formik.values.categoryId}
                onChange={(value) => {
                  setCategoryCreateError(null);
                  formik.setFieldValue("categoryId", value);
                }}
                onBlur={() => formik.handleBlur("categoryId")}
                messageError={categoryCreateError ?? errors.categoryId}
                allowCreate={canCreateCategory}
                onCreateOption={handleCreateCategory}
              />
            </div>
            <div className="space-y-1">
              <Label required>{t("LABEL.description")}</Label>
              <Input
                {...getFieldProps("description")}
                isInvalid={touched.description && errors.description}
                isValid={touched.description && !errors.description}
                messageError={errors.description}
              />
            </div>
            <div className="space-y-1">
              <Label required>{t("LABEL.payment method")}</Label>

              <div className="space-y-1">
                <Select
                  {...getFieldProps("payment_method")}
                  onValueChange={(value) => {
                    formik.setFieldValue("payment_method", value);
                  }}
                >
                  <SelectTrigger
                    isInvalid={touched.payment_method && errors.payment_method}
                    isValid={touched.payment_method && !errors.payment_method}
                  >
                    <SelectValue placeholder="Selecione a forma de pagamento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="Pix">Pix</SelectItem>
                      <SelectItem value="Money">Dinheiro</SelectItem>
                      <SelectItem value="CreditCard">Cartão de crédito</SelectItem>
                      <SelectItem value="DebitCard">Cartão de débito</SelectItem>
                      <SelectItem value="Ticket">Boleto</SelectItem>
                      <SelectItem value="AutomaticDebit">
                        Débito automático
                      </SelectItem>
                      <SelectItem value="BankDeposit">
                        Depósito bancário
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <MessageError
                  isInvalid={touched.payment_method && errors.payment_method}
                  messageError={errors.payment_method}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>{t("LABEL.maturity")}</Label>
              <DatePicker
                {...getFieldProps("date")}
                isInvalid={touched.date && !!errors.date}
                isValid={touched.date && !errors.date}
                messageError={errors.date}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("LABEL.value")}</Label>
              <MoneyInput
                placeholder="Valor"
                isInvalid={touched.value && errors.value}
                isValid={touched.value && !errors.value}
                {...getFieldProps("value")}
                messageError={errors.value}
              />
            </div>
            <div className="space-y-1">
              <Label>Multa por atraso (%)</Label>
              <Input
                type="number"
                placeholder="0%"
                isInvalid={touched.fine_for_delay && errors.fine_for_delay}
                isValid={touched.fine_for_delay && !errors.fine_for_delay}
                {...getFieldProps("fine_for_delay")}
                messageError={errors.fine_for_delay}
              />
            </div>
            <div className="space-y-1">
              <Label>Juros ao mês (%)</Label>
              <Input
                type="number"
                placeholder="0%"
                isInvalid={touched.interest_rate && errors.interest_rate}
                isValid={touched.interest_rate && !errors.interest_rate}
                {...getFieldProps("interest_rate")}
                messageError={errors.interest_rate}
              />
            </div>

            <div className="space-y-1">
              <Label>Parcelar ou repetir lançamento</Label>

              <div className="space-y-1">
                <Select
                  {...getFieldProps("mode")}
                  onValueChange={(value) => {
                    formik.setFieldValue("mode", value);
                  }}
                  defaultValue="unique"
                >
                  <SelectTrigger
                    isInvalid={touched.mode && errors.mode}
                    isValid={touched.mode && !errors.mode}
                  >
                    <SelectValue placeholder="Selecione a forma de pagamento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="unique">Não parcelar</SelectItem>
                      <SelectItem value="installment">Sim, parcelar</SelectItem>
                      <SelectItem value="periodic">
                        Sim, repetir periodicamente
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <MessageError
                  isInvalid={touched.mode && errors.mode}
                  messageError={errors.mode}
                />
              </div>
            </div>

            {(formik.values.mode === "periodic" ||
              formik.values.mode === "installment") && (
                <>
                  <div className="space-y-1">
                    <Label>{t("LABEL.period")}</Label>
                    <div className="space-y-1">
                      <Select
                        {...getFieldProps("type_periodic")}
                        onValueChange={(value) => {
                          formik.setFieldValue("type_periodic", value);
                        }}
                      >
                        <SelectTrigger
                          isInvalid={
                            touched.type_periodic && errors.type_periodic
                          }
                          isValid={touched.type_periodic && !errors.type_periodic}
                          loading={isLoadingCategories || !dataCategories}
                        >
                          <SelectValue placeholder="Selecione a categoria" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="daily">Diário</SelectItem>
                            <SelectItem value="weekly">Semanal</SelectItem>
                            <SelectItem value="biweekly">Bi-semanal</SelectItem>
                            <SelectItem value="monthly">Mensal</SelectItem>
                            <SelectItem value="annually">Anual</SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <MessageError
                        isInvalid={touched.type_periodic && errors.type_periodic}
                        messageError={errors.type_periodic}
                      />
                    </div>
                  </div>
                </>
              )}

            {formik.values.mode === "installment" && (
              <>
                <div className="space-y-1">
                  <Label>Parcelas</Label>
                  <div className="space-y-1">
                    <Select
                      {...getFieldProps("total_installments")}
                      onValueChange={(value) => {
                        formik.setFieldValue("total_installments", value);
                      }}
                    >
                      <SelectTrigger
                        isInvalid={
                          touched.total_installments &&
                          errors.total_installments
                        }
                        isValid={
                          touched.total_installments &&
                          !errors.total_installments
                        }
                        loading={isLoadingCategories || !dataCategories}
                      >
                        <SelectValue placeholder="Selecione o total de parcelas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {Array.from({ length: 96 }, (_, index) => (
                            <SelectItem key={index} value={String(index + 1)}>
                              {index + 1} Parcelas
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <MessageError
                      isInvalid={
                        touched.total_installments && errors.total_installments
                      }
                      messageError={errors.total_installments}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label>{t("LABEL.entry value")}</Label>
                  <Input
                    type="number"
                    placeholder="R$ 0,00"
                    isInvalid={touched.entry && errors.entry}
                    isValid={touched.entry && !errors.entry}
                    {...getFieldProps("entry")}
                    messageError={errors.entry}
                  />
                </div>
              </>
            )}
          </div>

          <div className="space-y-4">
            <div className="w-full border-b border-teal-500 py-5 flex items-center gap-1">
              <ChevronRight size={20} className="text-teal-700" />
              <h2 className="text-xl font-bold text-teal-700 mt-1">Anexo</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Descrição</Label>
                <Input
                  isInvalid={
                    touched.attachment_description &&
                    errors.attachment_description
                  }
                  isValid={
                    touched.attachment_description &&
                    !errors.attachment_description
                  }
                  {...getFieldProps("attachment_description")}
                  messageError={errors.attachment_description}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="entry_file">Anexo</Label>
                <Input
                  type="file"
                  id="entry_file"
                  isInvalid={
                    touched.attachment_path && errors.attachment_path
                      ? true
                      : false
                  }
                  isValid={touched.attachment_path && !errors.attachment_path}
                  onChange={(event) => {
                    const file = event.target.files && event.target.files[0];
                    if (file) {
                      setFile(file);
                      formik.setFieldValue("attachment_path", file);
                      const fileReader = new FileReader();
                      fileReader.onload = () => {
                        if (fileReader.readyState === 2) {
                          formik.setFieldTouched("file", true);
                          formik.setFieldValue(
                            "attachment_path",
                            fileReader.result
                          );
                        }
                      };
                      fileReader.readAsDataURL(file);
                    } else {
                      formik.setFieldValue("attachment_path", "");
                    }
                  }}
                  accept=".pdf, .png, .jpg"
                />
              </div>
            </div>
          </div>
        </div>

        <DrawerFooter className="flex flex-row justify-center pb-24 gap-2">
          <Button type="submit" className="px-10" isLoading={isLoading}>
            {t("LABEL.save")}
          </Button>
          <DrawerClose>
            <Button variant="outline">{t("LABEL.cancel")}</Button>
          </DrawerClose>
        </DrawerFooter>
      </div>
    </form>
  );
}
